import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Loader2, Search, ExternalLink, Trash2 } from "lucide-react";

export default function AdminLoyalty() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    adminApi
      .get("/admin/loyalty")
      .then((r) => setCards(r.data.cards || []))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (e, card) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      !window.confirm(
        `Supprimer définitivement la carte de ${card.name} (${card.email}) ?\n\nCette action est irréversible.`
      )
    )
      return;
    setDeletingId(card.id);
    try {
      await adminApi.delete(`/admin/loyalty/${card.id}`);
      setCards((prev) => prev.filter((c) => c.id !== card.id));
      toast.success(`Carte de ${card.name} supprimée`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Suppression impossible");
    } finally {
      setDeletingId(null);
    }
  };

  const filtered = cards.filter(
    (c) =>
      c.name.toLowerCase().includes(q.toLowerCase()) ||
      c.email.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="admin-loyalty-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl">Cartes fidélité</h1>
          <p className="text-sm text-amyu-muted mt-1">
            {cards.length} carte{cards.length > 1 ? "s" : ""} • 10 tampons = 1 tiramisu offert
          </p>
        </div>
        <div className="relative w-full md:w-72">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-amyu-muted" />
          <input
            data-testid="loyalty-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un client..."
            className="w-full bg-white rounded-full pl-11 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-coral/30"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24">
          <Loader2 className="animate-spin text-coral" size={28} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center text-amyu-muted">
          {cards.length === 0
            ? "Aucune carte fidélité pour le moment."
            : "Aucun résultat pour cette recherche."}
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map((c) => {
            const threshold = c.reward_threshold || 10;
            const pct = Math.min(100, (c.stamps / threshold) * 100);
            return (
              <Link
                key={c.id}
                to={`/s/${c.id}`}
                data-testid={`loyalty-card-row-${c.id}`}
                className="bg-white rounded-2xl p-5 flex items-center gap-5 hover:shadow-md transition-all"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg truncate">{c.name}</div>
                  <div className="text-xs text-amyu-muted truncate">{c.email}</div>
                  <div className="mt-2 h-2 bg-amyu-cream rounded-full overflow-hidden">
                    <div
                      className="h-full bg-coral"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-2xl text-coral">
                    {c.stamps}
                    <span className="text-sm text-amyu-muted">/{threshold}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted">
                    {c.total_redeemed} récomp.
                  </div>
                </div>
                <button
                  onClick={(e) => handleDelete(e, c)}
                  disabled={deletingId === c.id}
                  data-testid={`loyalty-delete-btn-${c.id}`}
                  title="Supprimer cette carte"
                  className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-amyu-muted hover:text-coral hover:bg-coral/10 transition-colors disabled:opacity-40"
                >
                  {deletingId === c.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                </button>
                <ExternalLink size={14} className="text-amyu-muted shrink-0" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
